/**
 * Central react-query retry policy (ISP-9999, nemesis-0824-systemic-4xx-retry-storm).
 *
 * WHY: `new QueryClient({})` inherited react-query's default `retry: 3`, which
 * retries EVERY failure — including definitive client errors (400/404) that can
 * NEVER succeed on a retry. Measured live: one bad detail nav (/wheel/bad-id,
 * /messages/bad-id, /gallery/bad-id, /builder/bad-id) fired 2–12 pointless
 * requests with exponential backoff, and delayed the user-facing error page
 * ~13–18s while the loader sat through the backoff.
 *
 * FIX: a 4xx short-circuits to zero retries and surfaces the error immediately.
 * Transient failures (5xx, network — no `error.response`) still recover, up to
 * MAX_TRANSIENT_RETRIES.
 *
 * Error shape: axios (this app uses axios ^1.6.0). An axios error carries the
 * HTTP status at `error.response.status`; a fallback to `error.status` covers a
 * non-axios error object defensively. A network error has no `.response`, so its
 * status is undefined → treated as transient (retryable).
 *
 * This is the SAME logic already inlined in services/capture.js (`captureRetry`);
 * this module is the app-wide default applied at the QueryClient level so every
 * detail route inherits it without a per-query opt-in.
 */

export const MAX_TRANSIENT_RETRIES = 2;

/** Read the HTTP status off an axios (or axios-shaped) error, or undefined. */
export const errorStatus = (error) => error?.response?.status ?? error?.status;

/** True when the status is a definitive client error (4xx) — never retry these. */
export const isDefinitiveClientError = (error) => {
  const s = errorStatus(error);
  return typeof s === "number" && s >= 400 && s < 500;
};

/**
 * react-query `retry` predicate. `failureCount` starts at 1 on the first failure.
 * - 4xx → false (0 retries, error surfaces immediately)
 * - everything else (5xx / network / no status) → retry up to MAX_TRANSIENT_RETRIES
 */
export const shouldRetryQuery = (failureCount, error) => {
  if (isDefinitiveClientError(error)) return false;
  return failureCount < MAX_TRANSIENT_RETRIES;
};
